/**
 * Switching donation popups by target.
 *
 * Every donation popup is for a donation target, and its form starts on that
 * target (lib/donation-popup-targets.php). Choosing another target in the
 * form swaps the popup for the one for it, so the picture and text beside the
 * form follow the choice. A target with no popup of its own leaves the popup
 * as it is.
 *
 * Every popup a swap can reach is already on the page, its form set up with
 * the page, so the swap opens one dialog and closes the other. What the donor
 * has filled in -- amount, name, email -- is carried over.
 */

const DIALOG = 'dialog.diakon-popup[data-diakon-donation-popup]';
const PURPOSE = 'select[name="purpose"]';

/**
 * The dialog a target swaps to.
 *
 * @param {string} target The chosen target.
 * @return {HTMLDialogElement|undefined} The dialog, or nothing.
 */
function findDialog(target) {
	return [...document.querySelectorAll(DIALOG)].find(
		(dialog) => dialog.dataset.diakonDonationFor === target
	);
}

/**
 * Copies what the donor has filled in from one form to the other.
 *
 * By name: the forms are the same block, so their fields match. Hidden fields
 * are the form's own and stay. A checkbox that changes is announced, so the
 * form shows or hides what hangs on it (the name fields).
 *
 * @param {HTMLFormElement} from Form left.
 * @param {HTMLFormElement} to   Form taken over.
 */
function copyFields(from, to) {
	[...from.elements].forEach((field) => {
		if (
			!field.name ||
			field.name === 'purpose' ||
			['hidden', 'submit', 'button'].includes(field.type)
		) {
			return;
		}

		const twin = [...to.elements].find(
			(other) =>
				other.name === field.name &&
				(field.type !== 'radio' || other.value === field.value)
		);

		if (!twin) {
			return;
		}

		if (field.type === 'radio') {
			if (field.checked) {
				twin.checked = true;
			}
		} else if (field.type === 'checkbox') {
			if (twin.checked !== field.checked) {
				twin.checked = field.checked;
				twin.dispatchEvent(new Event('change', { bubbles: true }));
			}
		} else {
			twin.value = field.value;
		}
	});
}

/**
 * Focuses the target dropdown's surroundings, not the dropdown itself:
 * focus() on a <select> opens the picker on iOS. A Tab from there lands on
 * the dropdown, where the donor was.
 *
 * @param {HTMLSelectElement} select Dropdown in the dialog just opened.
 */
function focusNear(select) {
	const anchor = select.parentElement ?? select;

	anchor.tabIndex = -1;
	anchor.focus({ preventScroll: true });
}

export function initDonationPopupSwitcher() {
	document.addEventListener('change', (event) => {
		const select = event.target;

		if (!select.matches?.(PURPOSE)) {
			return;
		}

		const from = select.closest(DIALOG);

		if (!from?.open || !select.form) {
			return;
		}

		const to = findDialog(select.value);
		const toSelect = to?.querySelector(PURPOSE);

		// Nowhere to go, or a form there that does not offer this target.
		if (
			!to ||
			to === from ||
			!toSelect?.form ||
			![...toSelect.options].some(
				(option) => option.value === select.value
			)
		) {
			return;
		}

		toSelect.value = select.value;
		copyFields(select.form, toSelect.form);

		// Focus goes back to what opened the first popup, wherever the donor
		// ends up closing.
		to.diakonOpener = from.diakonOpener;

		// Opened before the other closes, so the backdrop never drops.
		to.showModal();
		from.close();

		// The one left goes back to how it opened, for the next time.
		select.form.reset();
		select.form
			.querySelectorAll('input[type="checkbox"]')
			.forEach((box) =>
				box.dispatchEvent(new Event('change', { bubbles: true }))
			);

		focusNear(toSelect);
	});
}
